import { Search, Package, Library } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function ProductPickerModal({
  open,
  onOpenChange,
  products,
  productSearch,
  setProductSearch,
  isLoading,
  onSearch,
  onSelect,
  isAdmin,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Library className="w-5 h-5" />
            Select from Product Library
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Search */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  onSearch(e.target.value);
                }}
                className="pl-10"
                data-testid="product-search-input"
              />
            </div>
          </div>

          {/* Products List */}
          <ScrollArea className="h-80">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : products.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No products found</p>
                <Link to="/products" className="text-indigo-600 text-xs hover:underline">
                  Add products to library
                </Link>
              </div>
            ) : (
              <div className="space-y-2 pr-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="p-3 border rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors"
                    onClick={() => onSelect(product)}
                    data-testid={`picker-product-${product.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs font-mono">
                            {product.style_code || "—"}
                          </Badge>
                          <span className="font-medium truncate">
                            {product.brand} {product.product_name}
                          </span>
                        </div>
                        {product.available_colours?.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {product.available_colours.slice(0, 4).map((c) => (
                              <Badge key={c} variant="secondary" className="text-xs">
                                {c}
                              </Badge>
                            ))}
                            {product.available_colours.length > 4 && (
                              <Badge variant="secondary" className="text-xs">
                                +{product.available_colours.length - 4}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      {isAdmin && (
                        <span className="font-mono text-green-700 font-medium ml-3">
                          ${parseFloat(product.default_cost || 0).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          <div className="text-xs text-muted-foreground text-center">
            <Link to="/products" className="text-indigo-600 hover:underline">
              Manage Product Library
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ProductPickerModal;
